import { driveService, DriveFile } from './driveService';
import { memoryService } from './memoryService';
import { messageService } from './messageService';

interface SearchResult {
  id: string;
  title: string;
  description: string;
  type: 'document' | 'image' | 'video' | 'email' | 'file' | 'code' | 'archive' | 'memory';
  source: 'Drive' | 'Gmail' | 'Notion' | 'Local' | 'Memory';
  size?: string;
  modified: string;
  preview?: string;
  thumbnail?: string;
  url?: string;
  metadata?: any;
}

interface SearchResponse {
  results: SearchResult[];
  total: number;
  sources: {
    drive: number;
    email: number;
    memory: number;
    recent: number;
  };
}

interface RecentDocument {
  id: string;
  document_id: string;
  document_type: string;
  source: string;
  title: string;
  description?: string;
  mime_type?: string;
  file_size?: string;
  thumbnail_url?: string;
  direct_url?: string;
  metadata?: any;
  access_count: number;
  last_accessed_at: string;
}

const BACKEND_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

class SearchService {
  // Search across all sources
  async searchAll(
    query: string,
    options: {
      sources?: ('drive' | 'email' | 'memory' | 'recent')[];
      types?: string[];
      limit?: number;
      userId?: string;
    } = {}
  ): Promise<SearchResponse> {
    const {
      sources = ['drive', 'email', 'memory', 'recent'],
      types = [],
      limit = 50,
      userId
    } = options;

    const promises: Promise<SearchResult[]>[] = [];
    const searchPromises: { [key: string]: Promise<SearchResult[]> } = {};

    // Search Drive files
    if (sources.includes('drive')) {
      searchPromises.drive = this.searchDrive(query, limit);
    }

    // Search Emails
    if (sources.includes('email')) {
      searchPromises.email = this.searchEmails(query, limit);
    }

    // Search Memory
    if (sources.includes('memory') && userId) {
      searchPromises.memory = this.searchMemory(query, userId, limit);
    }

    // Search Recent Documents
    if (sources.includes('recent')) {
      searchPromises.recent = this.searchRecentDocuments(query, limit);
    }

    // Execute all searches in parallel
    const results = await Promise.allSettled(Object.values(searchPromises));
    
    let allResults: SearchResult[] = [];
    const sourceCounts = { drive: 0, email: 0, memory: 0, recent: 0 };

    // Combine results
    Object.keys(searchPromises).forEach((source, index) => {
      const result = results[index];
      if (result.status === 'fulfilled') {
        const sourceResults = result.value;
        allResults = allResults.concat(sourceResults);
        sourceCounts[source as keyof typeof sourceCounts] = sourceResults.length;
      } else {
        console.error(`Search failed for ${source}:`, result.reason);
      }
    });

    // Filter by types if specified
    if (types.length > 0) {
      allResults = allResults.filter(result => types.includes(result.type));
    }

    // Sort by relevance (this is a simple implementation)
    allResults.sort((a, b) => {
      // Prioritize exact title matches
      const aExactMatch = a.title.toLowerCase().includes(query.toLowerCase()) ? 1 : 0;
      const bExactMatch = b.title.toLowerCase().includes(query.toLowerCase()) ? 1 : 0;
      if (aExactMatch !== bExactMatch) {
        return bExactMatch - aExactMatch;
      }

      // Then sort by modified date (most recent first)
      return new Date(b.modified).getTime() - new Date(a.modified).getTime();
    });

    // Limit final results
    allResults = allResults.slice(0, limit);

    return {
      results: allResults,
      total: allResults.length,
      sources: sourceCounts
    };
  }

  // Search Google Drive files
  private async searchDrive(query: string, limit: number): Promise<SearchResult[]> {
    try {
      console.log('Searching Drive with query:', query);
      const driveResults = await driveService.searchFiles(query);
      console.log('Drive API returned:', driveResults);
      
      return driveResults.files.slice(0, limit).map((file: DriveFile): SearchResult => ({
        id: file.id,
        title: file.name,
        description: this.getFileDescription(file.mimeType),
        type: this.getDocumentType(file.mimeType),
        source: 'Drive',
        size: file.size || 'Unknown',
        modified: file.modifiedTime ? new Date(file.modifiedTime).toLocaleDateString() : 'Unknown',
        thumbnail: this.getFileIcon(file.mimeType),
        metadata: {
          mimeType: file.mimeType,
          fileId: file.id
        }
      }));
    } catch (error) {
      console.error('Drive search failed:', error);
      // If Drive is not authenticated, return empty results
      if (error instanceof Error && error.message?.includes('not authenticated')) {
        console.log('Drive not authenticated, returning empty results');
        return [];
      }
      return [];
    }
  }

  // Search emails through backend API
  private async searchEmails(query: string, limit: number): Promise<SearchResult[]> {
    try {
      const response = await fetch(`${BACKEND_URL}/api/documents/search/emails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          query,
          maxResults: limit
        })
      });

      if (!response.ok) {
        throw new Error(`Email search failed: ${response.status}`);
      }

      const data = await response.json();
      
      return (data.emails || []).map((email: any): SearchResult => ({
        id: email.id,
        title: email.subject || 'No Subject',
        description: email.snippet || email.parsedDate || '',
        type: 'email',
        source: 'Gmail',
        size: email.sizeEstimate ? `${Math.round(email.sizeEstimate / 1024)} KB` : undefined,
        modified: email.parsedDate || email.date || 'Unknown',
        thumbnail: '📧',
        metadata: {
          from: email.from,
          to: email.to,
          threadId: email.threadId,
          labels: email.labels,
          accountEmail: email.accountEmail
        }
      }));
    } catch (error) {
      console.error('Email search failed:', error);
      return [];
    }
  }

  // Search memory using memory service
  private async searchMemory(query: string, userId: string, limit: number): Promise<SearchResult[]> {
    try {
      const memoryData = await memoryService.getMemoryDataForQuery(userId, query);
      const results: SearchResult[] = [];

      // Add relevant messages
      if (memoryData.relevantMessages) {
        memoryData.relevantMessages.slice(0, Math.floor(limit / 2)).forEach((message: any) => {
          results.push({
            id: `memory-${message.id || Date.now()}`,
            title: `Memory: ${message.text?.substring(0, 50) || 'Conversation'}...`,
            description: message.text || 'Memory from conversation',
            type: 'memory',
            source: 'Memory',
            modified: message.timestamp ? new Date(message.timestamp).toLocaleDateString() : 'Unknown',
            thumbnail: '🧠',
            metadata: {
              conversationId: message.conversationId,
              role: message.role,
              relevanceScore: message.relevanceScore
            }
          });
        });
      }

      // Add mind map points if available
      if (memoryData.mindMap) {
        memoryData.mindMap.slice(0, Math.floor(limit / 2)).forEach((point: any, index: number) => {
          results.push({
            id: `mindmap-${index}`,
            title: `Concept: ${point.label || point.concept || 'Unknown'}`,
            description: point.description || 'Mind map concept',
            type: 'memory',
            source: 'Memory',
            modified: 'Recent',
            thumbnail: '🧭',
            metadata: {
              x: point.x,
              y: point.y,
              cluster: point.cluster
            }
          });
        });
      }

      return results;
    } catch (error) {
      console.error('Memory search failed:', error);
      return [];
    }
  }

  // Search recent documents through backend API
  private async searchRecentDocuments(query: string, limit: number): Promise<SearchResult[]> {
    try {
      const response = await fetch(`${BACKEND_URL}/api/documents/recent/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          query,
          limit
        })
      });

      if (!response.ok) {
        throw new Error(`Recent documents search failed: ${response.status}`);
      }

      const data = await response.json();
      
      return (data.documents || []).map((doc: RecentDocument): SearchResult => ({
        id: doc.document_id,
        title: doc.title,
        description: doc.description || `${doc.source} ${doc.document_type}`,
        type: this.mapDocumentType(doc.document_type),
        source: doc.source as any,
        size: doc.file_size || undefined,
        modified: new Date(doc.last_accessed_at).toLocaleDateString(),
        preview: doc.thumbnail_url,
        url: doc.direct_url,
        thumbnail: this.getSourceIcon(doc.source),
        metadata: {
          ...doc.metadata,
          accessCount: doc.access_count,
          documentType: doc.document_type
        }
      }));
    } catch (error) {
      console.error('Recent documents search failed:', error);
      return [];
    }
  }

  // Get recent documents without search
  async getRecentDocuments(limit: number = 20): Promise<SearchResult[]> {
    try {
      const response = await fetch(`${BACKEND_URL}/api/documents/recent?limit=${limit}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Recent documents fetch failed: ${response.status}`);
      }

      const data = await response.json();
      
      return (data.documents || []).map((doc: RecentDocument): SearchResult => ({
        id: doc.document_id,
        title: doc.title,
        description: doc.description || `${doc.source} ${doc.document_type}`,
        type: this.mapDocumentType(doc.document_type),
        source: doc.source as any,
        size: doc.file_size || undefined,
        modified: new Date(doc.last_accessed_at).toLocaleDateString(),
        preview: doc.thumbnail_url,
        url: doc.direct_url,
        thumbnail: this.getSourceIcon(doc.source),
        metadata: {
          ...doc.metadata,
          accessCount: doc.access_count,
          documentType: doc.document_type
        }
      }));
    } catch (error) {
      console.error('Recent documents fetch failed:', error);
      return [];
    }
  }

  // Track document access
  async trackDocumentAccess(document: SearchResult): Promise<void> {
    try {
      await fetch(`${BACKEND_URL}/api/documents/recent/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          document_id: document.id,
          document_type: this.mapToDocumentType(document.type, document.source),
          source: document.source,
          title: document.title,
          description: document.description,
          mime_type: document.metadata?.mimeType,
          file_size: document.size,
          thumbnail_url: document.preview,
          direct_url: document.url,
          metadata: document.metadata
        })
      });
    } catch (error) {
      console.error('Failed to track document access:', error);
    }
  }

  // Helper methods
  private getDocumentType(mimeType: string): SearchResult['type'] {
    if (mimeType.includes('image/')) return 'image';
    if (mimeType.includes('video/')) return 'video';
    if (mimeType.includes('text/') || mimeType.includes('document')) return 'document';
    if (mimeType.includes('application/zip') || mimeType.includes('archive')) return 'archive';
    if (mimeType.includes('javascript') || mimeType.includes('typescript')) return 'code';
    return 'file';
  }

  private mapDocumentType(dbType: string): SearchResult['type'] {
    switch (dbType) {
      case 'drive_file': return 'document';
      case 'email': return 'email';
      case 'notion_page': return 'document';
      case 'local_file': return 'file';
      default: return 'file';
    }
  }

  private mapToDocumentType(type: SearchResult['type'], source: string): string {
    if (source === 'Gmail') return 'email';
    if (source === 'Notion') return 'notion_page';
    if (source === 'Drive') return 'drive_file';
    return 'local_file';
  }

  private getFileDescription(mimeType: string): string {
    if (mimeType.includes('google-apps.document')) return 'Google Document';
    if (mimeType.includes('google-apps.spreadsheet')) return 'Google Spreadsheet';
    if (mimeType.includes('google-apps.presentation')) return 'Google Presentation';
    if (mimeType.includes('pdf')) return 'PDF Document';
    if (mimeType.includes('image/')) return 'Image File';
    if (mimeType.includes('video/')) return 'Video File';
    if (mimeType.includes('text/')) return 'Text File';
    return 'File';
  }

  private getFileIcon(mimeType: string): string {
    if (mimeType.includes('google-apps.document')) return '📄';
    if (mimeType.includes('google-apps.spreadsheet')) return '📊';
    if (mimeType.includes('google-apps.presentation')) return '📋';
    if (mimeType.includes('pdf')) return '📕';
    if (mimeType.includes('image/')) return '🖼️';
    if (mimeType.includes('video/')) return '🎥';
    if (mimeType.includes('text/')) return '📝';
    if (mimeType.includes('zip') || mimeType.includes('archive')) return '📦';
    return '📁';
  }

  private getSourceIcon(source: string): string {
    switch (source) {
      case 'Drive': return '💾';
      case 'Gmail': return '📧';
      case 'Notion': return '📋';
      case 'Local': return '💻';
      case 'Memory': return '🧠';
      default: return '📄';
    }
  }
}

export const searchService = new SearchService();
export type { SearchResult, SearchResponse }; 